/**
 * @author       grady@zumper.com (Grady Kuhnline <gradykuhnline>)
 * @copyright    Copyright (c) 2019, Zumper
 * @description
 */

import loadable, {
  DefaultComponent,
  ExtraComponentProps,
  LoadableComponent,
} from '@loadable/component'
import { IS_BROWSER } from '@zcode/env'
import { createFallback, Renderable } from '@zcode/render-props'
import hoistStatics from 'hoist-non-react-statics'
import React from 'react'

import { withReload } from './withReload'

export interface CreateLoadableSectionOptions<Props extends object> {
  errorFallback?: Renderable<Props>
  loadingFallback?: Renderable<Props>
  onLoadingError?: (error: unknown) => void
}
export type LoadFn<Props> = (props: Props) => Promise<DefaultComponent<Props>>
export type LoadableSection<Props extends object> = LoadableComponent<Props> & {
  reload: (props?: Props) => Promise<void>
}

export const createLoadableSection = <Props extends object>(
  loadFn: LoadFn<Props> | { requireAsync: LoadFn<Props> },
  {
    errorFallback,
    loadingFallback,
    onLoadingError,
  }: CreateLoadableSectionOptions<Props>
): LoadableSection<Props> => {
  let finalLoadFn: LoadFn<Props>

  // NOTE: the errorFallback completely replaces the loadable and won't clear itself properly if
  // the module loads successfully later. We need to track the error state and trigger a rerender
  // of the loadable if there is a success in the future. We do that by wrapping our loadable in a
  // `withReload` HOC that will force a rerender when reload is called.
  let loadError = false

  const ErrorFallback = createFallback(errorFallback)
  const LoadingFallback = createFallback(loadingFallback)

  // create a common error handler
  const handleLoadingError = (e: unknown) => {
    if (onLoadingError) {
      onLoadingError(e)
    }
    loadError = true
    return { default: ErrorFallback }
  }

  const handleSuccess = <T>(props: Props, module: T) => {
    // only trigger reload if we are likely showing the errorFallback
    if (loadError && ReloadableSection) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      ReloadableSection.reload(props)
    }
    loadError = false
    return module
  }

  // NOTE: loadable components relies on a custom object wrapper. Here we need to extend the
  // requireAsync function to handle loading errors.
  if (IS_BROWSER) {
    // On the browser we may not have the special function wrapper.
    if (typeof loadFn === 'function') {
      finalLoadFn = async (props: Props) => {
        const result = loadFn(props)
        return await result
          .then((module) => handleSuccess(props, module))
          .catch(handleLoadingError)
      }
    } else if (loadFn.requireAsync) {
      const originalRequireAsync = loadFn.requireAsync.bind(loadFn)
      loadFn.requireAsync = async (props: Props) =>
        await originalRequireAsync(props)
          .then((module) => handleSuccess(props, module))
          .catch(handleLoadingError)
      // @ts-expect-error loadable's types don't support {requireAsync}
      finalLoadFn = loadFn
    } else {
      // @ts-expect-error loadable's types don't support {requireAsync}
      finalLoadFn = loadFn
    }
  } else {
    // On the server we do not need to bother with the loading error fallback
    // @ts-expect-error loadable's types don't support {requireAsync}
    finalLoadFn = loadFn
  }
  const LoadableSection = loadable(finalLoadFn)
  const BetterLoadableSection = (props: Props & ExtraComponentProps) => {
    return React.createElement(LoadableSection, {
      fallback:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        props.fallback ||
        (loadingFallback ? React.createElement(LoadingFallback, props) : null),
      ...props,
    })
  }
  hoistStatics(BetterLoadableSection, LoadableSection)
  const ReloadableSection = withReload<Props>({
    loadingFallback: LoadingFallback,
    // @ts-expect-error can't tell that hoistStatics made BetterLoadableSection a LoadableSection
  })(BetterLoadableSection)
  return ReloadableSection
}
